import { Component, Input, OnInit } from '@angular/core';
import { ConfigurationFileDetailsDto } from "../../models/configuration-file-details/configuration-file-details-dto";
import { SimpleMessageService, Tenant, WebAppCommonModule } from "@allbesmart/web-app-common";
import { ConfigurationFileService } from "../../services/configuration-file.service";
import { Subscription } from "rxjs";
import { FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from "@angular/forms";

@Component({
    selector: 'oaibox-configuration-files-modal',
    templateUrl: './configuration-files-modal.component.html',
    styleUrls: ['./configuration-files-modal.component.css'],
    imports: [WebAppCommonModule, ReactiveFormsModule, FormsModule]
})
export class ConfigurationFilesModalComponent implements OnInit {

  @Input() public tenant?: Tenant;

  protected isSaving: boolean = false;
  protected isLoading: boolean = false;
  protected selectedConfigurationDetails?: ConfigurationFileDetailsDto;

  protected configurationFileForm: ConfigurationFileFormGroup;

  private getConfigurationFileSubscription?: Subscription;
  private configurationFileRequestsSubscription?: Subscription;

  protected searchKeyword?: string;
  protected shownConfigurationFiles: ConfigurationFileDetailsDto[] = [];
  protected availableConfigurationDetails: ConfigurationFileDetailsDto[] = [];

  constructor(
    private configurationFileService: ConfigurationFileService,
    private simpleMessageService: SimpleMessageService,
  ) {
    this.configurationFileForm = new FormGroup({
      filename: new FormControl<string | null>(null, [Validators.required]),
      description: new FormControl<string | null>(null),
      textFileContent: new FormControl<string | null>(null),
      binaryContent: new FormControl<Blob | null>(null),
    });
  }

  ngOnInit(): void {
    if (!this.tenant)
      throw new Error("Tenant not available!");

    this.configurationFileService.getConfigurationFileDetails$(this.tenant.id)
      .subscribe(availableConfigurationFiles => {
        this.availableConfigurationDetails = availableConfigurationFiles.sort((c1, c2) => c2.updatedAt - c1.updatedAt);
        this.shownConfigurationFiles = this.getFilteredConfigurationFiles(availableConfigurationFiles, this.searchKeyword);
      });
  }

  protected copyContent(copyButtonElement: HTMLElement): void {
    if (this.configurationFileForm.value.textFileContent) {
      navigator.clipboard.writeText(this.configurationFileForm.value.textFileContent);
      const oldHTML = copyButtonElement.innerHTML;
      copyButtonElement.innerHTML = `<i class="fa-solid fa-check-double success"></i> Copied`;
      setTimeout(() => copyButtonElement.innerHTML = oldHTML, 2000);
    }
  }

  protected cleanFileContent(): void {
    this.configurationFileForm.patchValue({textFileContent: null, binaryContent: null});
  }

  protected selectFileConfiguration(configurationFileDetailsDto: ConfigurationFileDetailsDto): void {
    if (!this.tenant)
      throw new Error("Tenant not available!");

    this.cleanSelectedConfigurationFile();
    this.selectedConfigurationDetails = configurationFileDetailsDto;
    this.configurationFileForm.patchValue({
      filename: this.selectedConfigurationDetails.filename,
      description: this.selectedConfigurationDetails.description,
    });

    this.isLoading = true;
    if (this.getConfigurationFileSubscription)
      this.getConfigurationFileSubscription.unsubscribe();
    this.getConfigurationFileSubscription = this.configurationFileService
      .getFileContend(this.tenant.id, configurationFileDetailsDto.id)
      .subscribe(fileBlob => {
        this.loadContent(fileBlob);
      });
  }

  protected createFileConfiguration(): void {
    if (!this.tenant)
      throw new Error("Tenant not available!");

    this.isSaving = true;
    if (this.configurationFileRequestsSubscription)
      this.configurationFileRequestsSubscription.unsubscribe();
    this.configurationFileRequestsSubscription = this.configurationFileService
      .createConfigurationFile(this.tenant.id, this.getFileFromContent(), this.configurationFileForm.value.description ?? undefined)
      .subscribe({
        next: configurationFileDetailsDto => {
          this.cleanSelectedConfigurationFile();
          this.simpleMessageService.showSimpleMessage({
            message: 'Configuration File created successfully!',
            icon: 'fa-solid fa-check-double',
            color: 'success',
          });

          this.availableConfigurationDetails.unshift(configurationFileDetailsDto);
          this.shownConfigurationFiles = this.getFilteredConfigurationFiles(this.availableConfigurationDetails, this.searchKeyword);
        },
        error: () => {
          this.isSaving = false;
          this.simpleMessageService.showSimpleMessage({
            message: 'Error while saving configuration file!',
            icon: 'fa-solid fa-triangle-exclamation',
            color: 'danger',
          });
        }
      });
  }

  protected updateFileConfiguration(): void {
    if (!this.tenant)
      throw new Error("Tenant not available!");
    if (!this.selectedConfigurationDetails)
      throw new Error("No configuration selected!");

    this.isSaving = true;
    if (this.configurationFileRequestsSubscription)
      this.configurationFileRequestsSubscription.unsubscribe();
    this.configurationFileRequestsSubscription = this.configurationFileService
      .updateConfigurationFile(this.tenant.id, this.selectedConfigurationDetails.id, this.getFileFromContent(), this.configurationFileForm.value.description ?? undefined)
      .subscribe({
        next: freshConfigurationFileDetailsDto => {
          this.cleanSelectedConfigurationFile();
          this.simpleMessageService.showSimpleMessage({
            message: 'Configuration File updated successfully!',
            icon: 'fa-solid fa-check-double',
            color: 'success',
          });

          const configurationFileOptional = this.availableConfigurationDetails
            .find(configurationFile => configurationFile.id === freshConfigurationFileDetailsDto.id);

          if (configurationFileOptional) {
            configurationFileOptional.filename = freshConfigurationFileDetailsDto.filename;
            configurationFileOptional.description = freshConfigurationFileDetailsDto.description;
            configurationFileOptional.updatedAt = freshConfigurationFileDetailsDto.updatedAt;
            configurationFileOptional.createdAt = freshConfigurationFileDetailsDto.createdAt;
          } else {
            this.availableConfigurationDetails.unshift(freshConfigurationFileDetailsDto);
          }

          this.availableConfigurationDetails.sort((c1, c2) => c2.updatedAt - c1.updatedAt);
          this.shownConfigurationFiles = this.getFilteredConfigurationFiles(this.availableConfigurationDetails, this.searchKeyword);
        },
        error: () => {
          this.isSaving = false;
          this.simpleMessageService.showSimpleMessage({
            message: 'Error while saving configuration file!',
            icon: 'fa-solid fa-triangle-exclamation',
            color: 'danger',
          });
        }
      });
  }

  protected deleteFileConfiguration(): void {
    this.getConfigurationFileSubscription?.unsubscribe();

    if (!this.tenant)
      throw new Error("Tenant not available!");
    if (!this.selectedConfigurationDetails)
      throw new Error("No configuration selected!");

    if (this.configurationFileRequestsSubscription) {
      this.configurationFileRequestsSubscription.unsubscribe();
      this.configurationFileRequestsSubscription = undefined;
    }

    this.configurationFileRequestsSubscription = this.configurationFileService
      .deleteConfigurationFile(this.tenant.id, this.selectedConfigurationDetails.id)
      .subscribe({
        next: () => {
          if (this.selectedConfigurationDetails) {
            this.availableConfigurationDetails.splice(this.availableConfigurationDetails.indexOf(this.selectedConfigurationDetails), 1);
            this.shownConfigurationFiles = this.getFilteredConfigurationFiles(this.availableConfigurationDetails, this.searchKeyword);
          }

          this.cleanSelectedConfigurationFile();
          this.simpleMessageService.showSimpleMessage({
            message: 'Configuration File deleted successfully!',
            icon: 'fa-solid fa-check-double',
            color: 'success',
          });
        },
        error: () => {
          this.simpleMessageService.showSimpleMessage({
            message: 'Error while deleting configuration file!',
            icon: 'fa-solid fa-triangle-exclamation',
            color: 'danger',
          });
        }
      });
  }

  protected cleanSelectedConfigurationFile(): void {
    this.isSaving = false;
    this.isLoading = false;
    this.selectedConfigurationDetails = undefined;
    this.configurationFileForm.reset();
  }

  protected fileInput(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.loadFile(file);
  }

  protected loadFile(file: File): void {
    this.configurationFileForm.patchValue({filename: file.name});
    this.loadContent(file);
  }

  private loadContent(blob: Blob): void {
    this.isLoading = true;
    blob.text().then(content => {
      if (this.contentIsBinary(content)) {
        this.configurationFileForm.patchValue({textFileContent: null, binaryContent: blob});
      } else {
        this.configurationFileForm.patchValue({textFileContent: content, binaryContent: null});
      }
      this.isLoading = false;
    });
  }

  private getFileFromContent(): File {
    if (!this.configurationFileForm.value.filename)
      throw new Error("Configuration file name not set");

    if (this.configurationFileForm.value.binaryContent) {
      return new File([this.configurationFileForm.value.binaryContent], this.configurationFileForm.value.filename);
    } else {
      return new File([this.configurationFileForm.value.textFileContent ?? ''], this.configurationFileForm.value.filename);
    }
  }

  private contentIsBinary(content: string): boolean {
    return /\ufffd/.test(content);
  }

  protected searchFiles(searchKeyword?: string) {
    this.searchKeyword = searchKeyword;
    this.shownConfigurationFiles = this.getFilteredConfigurationFiles(this.availableConfigurationDetails, searchKeyword);
  }

  private getFilteredConfigurationFiles(allFiles: ConfigurationFileDetailsDto[], searchKeyword?: string): ConfigurationFileDetailsDto[] {
    if (searchKeyword) {
      return allFiles.filter(configurationFile =>
        configurationFile.filename.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        configurationFile.description?.toLowerCase().includes(searchKeyword.toLocaleLowerCase()))
    } else {
      return [...allFiles];
    }
  }
}

type ConfigurationFileFormGroup = FormGroup<{
  filename: FormControl<string | null>,
  description: FormControl<string | null>,
  textFileContent: FormControl<string | null>,
  binaryContent: FormControl<Blob | null>,
}>;

